import React from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import Logo from '../Components/Logo';
import Loading from '../Components/Loading';
import UnavailableSystem from '../Components/UnavailableSystem';

import { useTimeModal } from '../Components/TimeModal/TimeModalContext';

import styles from '../styles/Index.module.css';

const Index = () => {
    const { openTimeModal } = useTimeModal();

    const [feedback, setFeedback] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [submissionCount, setSubmissionCount] = React.useState(0);
    const [firstSubmissionTime, setFirstSubmissionTime] = React.useState(null);

    const currentYear = new Date().getFullYear();

    const API_URL = process.env.REACT_APP_API_URL;
    console.log(API_URL);

    const { data: dbStatus, isLoading, isError, error } = useQuery({
    queryKey: ['dbStatus'],
    queryFn: async () => {
        try {
            const response = await axios.get(`/api/health`);
            if (response.status === 200 && response.data.status === 'success') {
                return 'connected';
            } else {
                throw new Error('Некорректный ответ от сервера');
            }
            } catch (err) {
            if (err.response) {
                throw new Error(err.response.data.message || 'Ошибка сервера');
            } else if (err.request) {
                throw new Error('Сервер недоступен. Проверьте соединение');
            } else {
                throw new Error('Ошибка при выполнении запроса');
            }
        }
    },
    retry: 2,
    refetchOnWindowFocus: false,
    });

    if (isLoading) {
        return (
            <Loading />
        );
    }

    if (isError) {
    return (
        <UnavailableSystem dbStatus={dbStatus} />
    );
    }

    const sendFeedback = (e) => {
        e.preventDefault();

        const now = new Date().getTime();

        if (firstSubmissionTime && now - firstSubmissionTime > 20 * 60 * 1000) {
            setSubmissionCount(0);
            setFirstSubmissionTime(null);
        }

        if (submissionCount >= 2) {
            openTimeModal('Вы превысили лимит обращений. Пожалуйста, попробуйте снова через 20 минут.', 'error');
            return;
        }

        if (submissionCount === 0) {
            setFirstSubmissionTime(now);
        }

        setSubmissionCount(submissionCount + 1);

        axios.post(`/api/feedback?email=${email}&message=${feedback}`)
        .then(() => {
          openTimeModal('Обращение успешно отправлено', 'success');
          setEmail('');
          setFeedback('');
        })
        .catch((error) => {
          openTimeModal(`Ошибка отправки обращения:  ${error.response?.data.message || error.message}`, 'error');
        });
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLimiter}>
                    <Logo to='/' position='relative' />
                    <a href='/auth/login' type='button' className='button'>Войти в учётную запись</a>
                </div>
            </div>

            <div className={styles.main}>
                <div className={styles.headLine}>
                    <img src='/img/logo/mainlogo-without-text-375.png' alt='Система Поток' />
                    <h1>Система «Поток»</h1>
                    <h2>Следите за текущим этапом исполнения дела,<br/>получайте уведомления, которые помогут закрывать<br/>всё в срок.</h2>
                    <a href='/auth/registration' type='button' className='button black'>Начать использование</a>
                </div>

                <div className={styles.headContainer}>
                    <div className={styles.phaseName}>Начало</div>
                    <h1>Зарегистрируйтесь в системе</h1>
                    <a className={styles.phaseDescription}>Перейдите по кнопке "Начать использование" и зарегистрируйтесь в системе.</a>
                    <a className={styles.phaseDescription}>Если вы уже сотрудничаете с СОАУ «Альянс» подтверждение учётной записи произойдёт автоматически.</a>
                    <a className={styles.phaseDescription}>Иначе следует подождать.</a>
                </div>
                <div className={styles.headContainer}>
                    <div className={styles.phaseName}>Функциональность</div>
                    <h1>Система покажет предлагаемые вам СОАУ «Альянс» арбитражные дела</h1>
                    <a className={styles.phaseDescription}>Для этого после входа перейдите по кнопке "Заявки" и просмотрите все дела, которые вам предложено вести.</a>
                    <a className={styles.phaseDescription}>Нажмите кнопку "Претендовать на ведение дела" у соответствующей строки и ожидайте решения СОАУ «Альянс».</a>
                    <a className={styles.phaseDescription}>Система автоматически отправит ваше намерение.</a>
                    <img src='/img/background/instruction-w-back-v2.png' alt='Фоновое изображение' />
                    <a className={styles.aboutData}>*Данные дела в иллюстрации вымышлены.</a>
                </div>
                <div className={styles.headContainer}>
                    <h1>После назначения вы сможете просматривать дела в Журнале</h1>
                    <a className={styles.phaseDescription}>Система в автоматическом режиме поможет управлять арбитражными делами и подскажет о сроках исполнения каждого этапа.</a>
                    <a className={styles.phaseDescription}>По нажатию на каждую строку таблицы открывается карточка с подробными сведениями.</a>
                    <a className={styles.phaseDescription}>Вы можете отметить выполнение этапа и, если все процедуры по делу были выполнены в срок, закрыть его в системе.</a>
                    <a className={styles.phaseDescription}>Эти действия автоматически отобразятся у СОАУ «Альянс».</a>
                    <a className={styles.phaseDescription} style={{color: '#a6a5a2'}}>Вам не придется совершать дополнительных действий.</a>
                    <img src='/img/background/about-home-page-and-card.png' alt='Фоновое изображение' />
                    <a className={styles.aboutData}>*Данные дел в представленной таблице вымышлены.</a>
                </div>
                <div className={styles.headContainer}>
                    <div className={styles.phaseName}>Уведомления</div>
                    <h1>Система оповестит по почте о неисполнении этапа дела в срок</h1>
                    <a className={styles.phaseDescription}>Если исполняемый этап не будет выполнен в срок</a>
                    <a className={styles.phaseDescription}>вы получите письмо с уведомлением на email.</a>
                    <a className={styles.phaseDescription}>Также все уведомления дублируются на странице "Уведомления".</a>
                    <img src='/img/background/about-notification.png' alt='Фоновое изображение' />
                    <a className={styles.aboutData}>*Данные дел вымышлены.</a>
                </div>
                <div className={styles.headContainer}>
                    <h1>Что если вы не хотите получать письма о неисполнении дела в срок?</h1>
                    <a className={styles.phaseDescription}>Вы всегда можете зайти в настройки слева на главной странице.</a>
                    <a className={styles.phaseDescription}>Отключите ползунок "Получать уведомления о неисполнении дела в срок".</a>
                    <a className={styles.phaseDescription} style={{color: '#a6a5a2'}}>Более к вам не будут приходить уведомления данного типа.</a>
                    <img src='/img/background/about-home-page-and-card-v2.png' alt='Фоновое изображение' />
                    <a className={styles.aboutData}>*Данные дел в представленной таблице вымышлены.</a>
                </div>
                <div className={styles.headContainer}>
                    <div className={styles.phaseName}>Остались вопросы или возникли проблемы?</div>
                    <form className={styles.formFeedback} onSubmit={(e) => sendFeedback(e)}>
                        <div className={styles.formCont}>
                            <h1>Обратитесь в службу поддержки</h1>
                            <a>Введите email для связи</a>
                            <input type='email' placeholder='Email' value={email} onChange={(e) => setEmail(e.target.value)} required />
                            <a>Введите обращение в поддержку</a>
                            <textarea rows="10" placeholder="Опишите проблему" minlength="8" maxlength="10000" require="" value={feedback} onChange={(e) => setFeedback(e.target.value)}></textarea>
                            <button type='submit' className='button black'>Отправить</button>
                        </div>
                    </form>
                </div>
            </div>

            <div className={styles.footer}>
                <div className={styles.footerLimiter}>
                    <div className={styles.columnContainer}>
                        <a href="https://alians009.ru/"><img style={{width: '55px', marginRight: '30px'}} src='/img/logo/alians-logo.png' alt='Логотип СОАУ "Альянс"' /></a>
                        <div style={{fontSize: '14px'}}>Система «Поток» - сервис компании СОАУ «Альянс»</div>
                        <div style={{fontSize: '14px'}}>&copy; {currentYear} СОАУ  «Альянс», 603000, г. Нижний Новгород, ул. Горького, д. 52, помещение П2</div>
                    </div>
                    <div className={styles.columnContainer}>
                        <a href="https://alians009.ru/">Перейти на сайт «Альянс»</a>
                    </div>
                    <div className={styles.columnContainer}>
                        <a href="/privacy-policy">Пользовательское соглашение и политика конфиденциальности</a>
                        <p>По вопросам изменения<br/>или удаления данных<br/>из системы:<br/><a href='mailto:journal.alians@yandex.ru'>journal.alians@yandex.ru</a></p>
                        <p>По юридическим вопросам:<br/><a href='mailto:schepetov_av@mail.ru'>schepetov_av@mail.ru</a></p>
                    </div>
                    <div className={styles.columnContainer}>
                        <a href="/auth/registration">Создать учётную запись</a>
                        <a href="/auth/login">Войти в учётную запись</a>
                        <a href="/feedback">Отправить обращение<br/>в поддержку</a>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Index;